import React, { useRef, useEffect, useState } from "react";
import { ReactComponent as InfoIcon } from "../../../../../../assets/fi-rr-info.svg";
import { ReactComponent as DateIcon } from "../../../../../../assets/fi-rr-calendar.svg";
import { DatePicker } from "@mantine/dates";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import base_url from "../../../../../../api/bootapi";
import AsyncSelect from "react-select/async";
import CustomButton from "../../../../../../components/CustomButton";

const FlightDetails = ({
  setSteptwo,
  onSubmit,
  selectedDeparture,
  selectedArrival,
  setSelectedDeparture = () => {},
  setSelectedArrival = () => {},
}) => {
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("flightDetailsData");
    if (savedData) {
      const parsedData = JSON.parse(savedData);

      // Set form values from localStorage
      if (parsedData.flight_date) {
        setValue("flight_date", parsedData.flight_date);
      }
      if (parsedData.departureAirport) {
        setValue("departureAirport", parsedData.departureAirport);
        setSelectedDeparture(parsedData.departureAirport);
      }
      if (parsedData.arrivalAirport) {
        setValue("arrivalAirport", parsedData.arrivalAirport);
        setSelectedArrival(parsedData.arrivalAirport);
      }
      if (parsedData.airlineName) {
        setValue("airlineName", parsedData.airlineName);
      }
      if (parsedData.flightNumber) {
        setValue("flightNumber", parsedData.flightNumber);
      }
      if (parsedData.numberOfPassengers) {
        setValue("numberOfPassengers", parsedData.numberOfPassengers);
      }
    } else {
      // Fallback to props if no localStorage data
      if (selectedDeparture) {
        setValue("departureAirport", selectedDeparture);
      }
      if (selectedArrival) {
        setValue("arrivalAirport", selectedArrival);
      }
    }
  }, [
    setValue,
    setSelectedDeparture,
    setSelectedArrival,
    selectedDeparture,
    selectedArrival,
  ]);

  // Auto-save form data to localStorage whenever it changes
  useEffect(() => {
    const subscription = watch((value) => {
      localStorage.setItem("flightDetailsData", JSON.stringify(value));
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const dateRef = useRef(null);
  const [isOpentwo, setIsOpentwo] = useState(false);

  const flightDate = watch("flight_date");
  const departureAirport = watch("departureAirport");
  const arrivalAirport = watch("arrivalAirport");
  const airlineName = watch("airlineName");
  const flightNumber = watch("flightNumber");
  const numberOfPassengers = watch("numberOfPassengers");

  // Form validation logic
  useEffect(() => {
    const isValid =
      flightDate &&
      departureAirport?.value &&
      arrivalAirport?.value &&
      airlineName?.value &&
      flightNumber &&
      numberOfPassengers;

    setSteptwo(!!isValid);
  }, [
    flightDate,
    departureAirport,
    arrivalAirport,
    airlineName,
    flightNumber,
    numberOfPassengers,
    setSteptwo,
  ]);

  const handleMaxLength = (e) => {
    let inputVal = e.target.value.replace(/\D/g, ""); // remove non-digits
    if (inputVal.length > 3) {
      inputVal = inputVal.slice(0, 3); // limit to 3 digits
    }

    setValue("numberOfPassengers", inputVal, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleFormSubmit = (data) => {
    onSubmit(data); // Pass the form data to the parent component
  };

  const handleSelect = (selectedDate) => {
    if (selectedDate) {
      const formattedDate = selectedDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
      setValue("flight_date", formattedDate); // Update form value
      setIsOpentwo(false); // Close the date picker
    }
  };

  // Fetch initial data
  useEffect(() => {
    fetchAllAirportslist();
    fetchAllAirlineNamelist();
  }, []);

  const [airlineOptions, setAirlineOptions] = useState([]);

  const loadAirlineNameOptions = async (inputValue) => {
    const airlines = await fetchAllAirlineNamelist(inputValue); // Pass inputValue to fetchAllAirlineNamelist
    return airlines;
  };

  const fetchAllAirlineNamelist = async (searchTerm = "") => {
    try {
      const response = await axios.get(
        `${base_url.api1}/tourapp/flight_states_airlines?sortby=airline_name`,
        {
          params: {
            search: searchTerm, // Pass searchTerm as a query parameter
          },
        }
      );

      // Process the response data
      const processedAirlines = response.data.Airlines.map((airline) => ({
        label: `${airline.airline_name} ${
          airline.iata_code ? `(${airline.iata_code})` : ""
        }`,
        value: airline.airline_name,
      }));

      return processedAirlines;
    } catch (error) {
      console.error("Error fetching airlines:", error);
      return [];
    }
  };

  const [airportOptions, setAirportOptions] = useState([]);

  const loadAirportsOptions = async (inputValue) => {
    const airports = await fetchAllAirportslist(inputValue); // Pass inputValue to fetchAllAirportslist
    return airports;
  };

  const fetchAllAirportslist = async (searchTerm = "") => {
    try {
      const response = await axios.get(
        `${base_url.api1}/tourapp/flight_states_airports?sortby=airport_name`,
        {
          params: {
            search: searchTerm, // Pass searchTerm as a query parameter
          },
        }
      );

      // Process the response data
      const processedAirports = response.data.Airports.map((airport) => ({
        label: `${airport.airport_name.replace(
          /[\n\u0000-\u001F\u007F-\u009F]/g,
          ""
        )} (${airport.iata_code})`,
        value: airport.iata_code,
      }));

      return processedAirports;
    } catch (error) {
      console.error("Error fetching airports:", error);
      return [];
    }
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      background: "none !important",
      outline: "none !important",
      border: "none !important",
      boxShadow: "none !important",
      // width: "280px",
      // minWidth: "280px",
      // maxWidth: "280px",
      width: "100%",
      minWidth: "100%",
      maxWidth: "100%",
    }),
    menu: (base) => ({
      ...base,
      background: "#ffffff",
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      background: isSelected
        ? "#007bff"
        : isFocused
        ? "#f0f0f0"
        : "transparent",
      color: isSelected ? "#ffffff" : "#000000", // Ensure selected option is visible
    }),
  };

  return (
    <div className="stepper-container">
      <form onSubmit={handleSubmit(handleFormSubmit)} id="flight-details-form">
        <div className="form-header-text-wrapper">
          <h3 className="stepper-header" style={{ margin: 0 }}>
            Flight details
          </h3>
          {/* <span className="info-wrapper">
            <InfoIcon /> What information do we need?
          </span> */}
        </div>
      </form>

      <div className="new-form-wrapper">
        <div className="grid-two">
          <div className="custom-date" style={{ width: "100%" }} ref={dateRef}>
            <span className="date-label">
              Flight date<span style={{ color: "#de8385" }}> *</span>
            </span>
            <div
              className="date-box"
              onClick={() => setIsOpentwo(!isOpentwo)}
              style={{
                borderColor: isOpentwo ? "#314064" : "#E6F6FF",
                backgroundColor: "#e8eef7",
              }}
            >
              <span className="date-text">
                {watch("flight_date") || "Choose date"}
              </span>
              <DateIcon />
            </div>
            {isOpentwo && (
              <div className={`date-wrapper bottom-date}`}>
                <span
                  className="sub-header"
                  style={{ color: "#222222", marginBottom: "10px" }}
                >
                  Select date
                </span>
                <DatePicker
                  defaultValue={new Date()}
                  size="sm"
                  value={
                    watch("flight_date") ? new Date(watch("flight_date")) : null
                  }
                  onChange={(date) => {
                    // Get the date in local timezone without time conversion
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");
                    const formattedDate = `${month}-${day}-${year}`; // YYYY-MM-DD
                    setValue("flight_date", formattedDate); // Update form value
                    setIsOpentwo(false); // Close the date picker
                  }}
                />
                <div
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                  }}
                >
                  <CustomButton
                    type="secondary"
                    btnText={"cancel"}
                    flex={true}
                    onClick={() => {
                      setValue("flight_date", null);
                      setIsOpentwo(false);
                    }}
                  />
                  <CustomButton
                    btnText={"apply"}
                    flex={true}
                    onClick={() => setIsOpentwo(false)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <h4 className="stepper-sub-header" style={{ marginBottom: "15px" }}>
            Flight itinerary
          </h4>
          <div className="grid-two">
            <div className="custom-select" style={{ width: "100%" }}>
              <span className="select-label">
                Departed from<span style={{ color: "#de8385" }}> *</span>
              </span>
              <div
                className="select-box"
                style={{
                  borderColor: "#E6F6FF",
                  backgroundColor: "#e8eef7",
                }}
              >
                <Controller
                  name="departureAirport"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <AsyncSelect
                      {...field}
                      cacheOptions
                      defaultOptions
                      loadOptions={loadAirportsOptions} // Use the updated loadOptions function
                      placeholder="Flight from"
                      value={field.value}
                      // value={selectedDeparture}
                      onChange={(selectedOption) => {
                        field.onChange(selectedOption); // Update the form value
                        if (selectedDeparture) {
                          setSelectedDeparture(selectedOption);
                        }
                      }}
                      styles={selectStyles}
                    />
                  )}
                />
                {errors.departureAirport && (
                  <p className="errormessage">
                    {errors.departureAirport.message}
                  </p>
                )}
              </div>
            </div>

            <div className="custom-select" style={{ width: "100%" }}>
              <span className="select-label">
                Arrival airport<span style={{ color: "#de8385" }}> *</span>
              </span>
              <div
                className="select-box"
                style={{
                  borderColor: "#E6F6FF",
                  backgroundColor: "#e8eef7",
                }}
              >
                <Controller
                  name="arrivalAirport"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <AsyncSelect
                      {...field}
                      cacheOptions
                      defaultOptions
                      loadOptions={loadAirportsOptions}
                      placeholder="Flight to"
                      value={field.value}
                      // value={selectedArrival}
                      onChange={(selectedOption) => {
                        field.onChange(selectedOption);
                        if (selectedArrival) {
                          setSelectedArrival(selectedOption);
                        }
                      }}
                      styles={selectStyles}
                    />
                  )}
                />
                {errors.arrivalAirport && (
                  <p className="errormessage">
                    {errors.arrivalAirport.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div>
          <h4 className="stepper-sub-header" style={{ marginBottom: "15px" }}>
            Choose your flight
          </h4>
          <div className="grid-two">
            <div className="custom-select" style={{ width: "100%" }}>
              <span className="select-label">
                Airline name<span style={{ color: "#de8385" }}> *</span>
              </span>
              <div
                className="select-box"
                style={{
                  borderColor: "#E6F6FF",
                  backgroundColor: "#e8eef7",
                }}
              >
                <Controller
                  name="airlineName"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <AsyncSelect
                      {...field}
                      cacheOptions
                      defaultOptions
                      loadOptions={loadAirlineNameOptions}
                      placeholder="Choose Airline"
                      onChange={(selectedOption) => {
                        field.onChange(selectedOption);
                      }}
                      styles={selectStyles}
                    />
                  )}
                />
                {errors.airlineName && (
                  <p className="errormessage">{errors.airlineName.message}</p>
                )}
              </div>
            </div>
            <div className="custom-input" style={{ width: "100%" }}>
              <label className="input-label">
                Flight number<span style={{ color: "#de8385" }}> *</span>
              </label>
              <Controller
                name="flightNumber"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field }) => (
                  <input
                    {...field}
                    className="input-box"
                    placeholder="Enter flight number"
                    type="text"
                  />
                )}
              />
              {errors.flightNumber && (
                <p className="errormessage">{errors.flightNumber.message}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <h4 className="stepper-sub-header" style={{ marginBottom: "5px" }}>
            How many passengers are included in this claim?
          </h4>
          <h6 className="sub-header" style={{ marginBottom: "15px" }}>
            Each passenger may be eligible for separate compensation.
          </h6>
          <div className="grid-two">
            <div className="custom-input" style={{ width: "100%" }}>
              <label className="input-label">
                Number of passengers<span style={{ color: "#de8385" }}> *</span>
              </label>
              <Controller
                name="numberOfPassengers"
                control={control}
                rules={{
                  required: "This field is required",
                  min: {
                    value: 1,
                    message: "Minimum value is 1",
                  },
                  max: {
                    value: 999,
                    message: "Maximum value is 999",
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    className="input-box"
                    placeholder="Enter number of passengers"
                    inputMode="numeric"
                    value={numberOfPassengers}
                    onChange={handleMaxLength}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                )}
              />
              {errors.numberOfPassengers && (
                <p className="errormessage">
                  {errors.numberOfPassengers.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightDetails;
